import { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useAlert } from 'react-alert'

import {
  createNewService,
  updateService,
  fetchTypeService,
  fetchConclusionService,
  fetchAllClinics
} from '../../../main/usecases'
import {
  ClinicsModel,
  ConclusionServiceModel,
  TypeToAvaliacaoModel
} from '../../../domain/models'

import { goToUrl, maskCpf, dateToday } from '../../utils'
import { useModalBox, useOnChangeDetect } from '../../contexts'
import { Button, Input, SelectInput, Textarea } from '..'
import { FormStyled } from '../timeline/modal/session/style'

type ServiceFormModel = {
  value?: any
  service?: any
}

const ServiceForm = ({ value, service }: ServiceFormModel) => {
  const [typeToService, setTypeToService] = useState<TypeToAvaliacaoModel[]>()
  const [loadClinics, setLoadClinics] = useState<ClinicsModel[]>()
  const [conclusionToService, setConclusionToService] =
    useState<ConclusionServiceModel[]>()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState({
    cpf: maskCpf(value?.cpf),
    name: value?.name || value?.nome,
    dataServico: service ? service?.dataServico : dateToday,
    dataFinal: service ? service?.dataFinal : '',
    demanda: '',
    cdTipoAvaliacao: null,
    cdClinica: null,
    cdConclusaoServico: null,
    objetivo: '',
    anotacoes: ''
  })
  const history = useHistory()
  const { modalClose } = useModalBox()
  const alert = useAlert()
  const { setRefresh } = useOnChangeDetect()

  const optionsTypeService = useMemo(
    () =>
      typeToService?.map((item) => {
        return { value: item?.cdTipoAvaliacao, label: item?.tipoAvaliacao }
      }),
    [typeToService]
  )

  const optionsClinics = useMemo(
    () =>
      loadClinics
        ?.filter((i) => i.nomeClinica)
        ?.map((item) => {
          return { value: item?.cdClinica, label: item?.nomeClinica }
        }),
    [loadClinics]
  )

  const optionsConclusionService = useMemo(
    () =>
      conclusionToService?.map((item) => {
        return {
          value: item?.cdConclusaoAvaliacao,
          label: item?.conclusaoAvaliacao
        }
      }),
    [conclusionToService]
  )

  useEffect(() => {
    setData((prev: any) => ({
      ...prev,
      cdClinica: optionsClinics && optionsClinics[0]?.value?.toString()
    }))
  }, [optionsClinics])

  useEffect(() => {
    ;(async () => {
      try {
        const responseToTypeService = await fetchTypeService().load()
        setTypeToService(responseToTypeService)
        const responseToLoadClinics = await fetchAllClinics().load()
        setLoadClinics(responseToLoadClinics)
        const responseToConclusionService =
          await fetchConclusionService().load()
        setConclusionToService(responseToConclusionService)
        setData((prev) => ({
          ...prev,
          cdTipoAvaliacao: responseToTypeService.cdTipoAvaliacao
        }))

        if (service) {
          setData((prev) => ({
            ...prev,
            ...service,
            cdTipoAvaliacao: service.tipoServico.cdTipoAvaliacao,
            cdClinica: service?.clinica?.cdClinica,
            cdConclusaoServico: service?.conclusaoServico?.cdConclusaoServico
          }))
        }
      } catch (error) {
        console.log(error)
      }
    })()
  }, [service])

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    const body = {
      cliente: {
        cdCliente: value?.code || value?.cdCliente
      },
      dataServico: data.dataServico,
      dataFinal: data.dataFinal,
      demanda: data.demanda,
      tipoServico: {
        cdTipoAvaliacao: data.cdTipoAvaliacao,
        tipoServico: typeToService?.find(
          (item) =>
            String(item.cdTipoAvaliacao) === String(data.cdTipoAvaliacao)
        )?.tipoAvaliacao
      },
      ...(data.cdClinica !== undefined && {
        clinica: {
          cdClinica: data.cdClinica,
          nomeClinica: loadClinics?.find(
            (item) => String(item.cdClinica) === String(data.cdClinica)
          )?.nomeClinica
        }
      }),
      ...(data.cdConclusaoServico !== '' &&
      data.cdConclusaoServico !== undefined
        ? {
            conclusaoServico: service && {
              cdConclusaoServico: data.cdConclusaoServico,
              conclusaoServico: conclusionToService?.find(
                (item) =>
                  String(item.cdConclusaoAvaliacao) ===
                  String(data.cdConclusaoServico)
              )?.conclusaoAvaliacao
            }
          }
        : { conclusaoServico: null }),
      objetivo: data.objetivo,
      anotacoes: data.anotacoes
    }

    try {
      setLoading(true)
      if (service) {
        await updateService(service.cdServico).update(body)
        modalClose(true)
        alert.show('Serviço atualizado com sucesso', {
          type: 'success',
          timeout: 4000
        })
        setRefresh((prev: any) => !prev)
      } else {
        if (value?.cdCliente) {
          await createNewService().post(body)
          modalClose(true)
          alert.show('Serviço criado com sucesso', {
            type: 'success',
            timeout: 4000
          })
          setRefresh((prev: any) => !prev)
        } else {
          await createNewService().post(body)
          modalClose(true)
          alert.show('Serviço criado com sucesso', {
            type: 'success',
            timeout: 4000
          })
          goToUrl(history, `/meus-pacientes/${value?.code}/prontuario`)
        }
      }
    } catch (error) {
      console.error(error)
      alert.show('Erro ao criar um serviço', {
        type: 'error',
        timeout: 4000
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <FormStyled onSubmit={onSubmit} className="form-service">
      <Input
        labelColor="#4F4F4F"
        label="CPF*"
        name="cpf"
        type="text"
        onChange={(e: any) =>
          setData((prev) => ({ ...prev, cpf: e.target.value }))
        }
        value={data.cpf}
        required
        disabled
      />
      <Input
        labelColor="#4F4F4F"
        label="Nome*"
        name="name"
        type="text"
        onChange={(e: any) =>
          setData((prev) => ({ ...prev, name: e.target.value }))
        }
        required
        value={data.name}
        disabled
      />
      <Input
        labelColor="#4F4F4F"
        label="Data inicial*"
        name="initial-date"
        type="date"
        max={'2222-10-29'}
        onChange={(e: any) =>
          setData((prev) => ({ ...prev, dataServico: e.target.value }))
        }
        defaultValue={data.dataServico}
      />
      {service && (
        <Input
          labelColor="#4F4F4F"
          label="Data final"
          name="end-date"
          type="date"
          max={'2222-10-29'}
          onChange={(e: any) =>
            setData((prev) => ({ ...prev, dataFinal: e.target.value }))
          }
          defaultValue={data.dataFinal}
        />
      )}
      <Input
        labelColor="#4F4F4F"
        label="Demanda*"
        name="demand"
        type="text"
        required
        maxLength={100}
        onChange={(e: any) =>
          setData((prev) => ({ ...prev, demanda: e.target.value }))
        }
        value={data.demanda}
      />
      <SelectInput
        labelColor="#4F4F4F"
        label="Tipo*"
        name="type"
        onChange={(e: any) =>
          setData((prev) => ({ ...prev, cdTipoAvaliacao: e.target.value }))
        }
        options={optionsTypeService}
        required
        value={data.cdTipoAvaliacao || ''}
      />
      {!!optionsClinics?.length && (
        <SelectInput
          labelColor="#4F4F4F"
          label="Clínicas"
          name="clinics"
          hasSelectOption={false}
          onChange={(e: any) =>
            setData((prev) => ({ ...prev, cdClinica: e.target.value }))
          }
          options={optionsClinics}
          value={data.cdClinica || ''}
        />
      )}
      <Input
        labelColor="#4F4F4F"
        label="Objetivo"
        name="objective"
        type="text"
        onChange={(e: any) =>
          setData((prev) => ({ ...prev, objetivo: e.target.value }))
        }
        value={data.objetivo}
      />
      {service && (
        <SelectInput
          hasAvaibleDefault
          hasSelectOption={false}
          labelColor="#4F4F4F"
          label="Conclusão"
          name="conclusion"
          onChange={(e: any) =>
            setData((prev) => ({
              ...prev,
              cdConclusaoServico: e.target.value
            }))
          }
          options={optionsConclusionService}
          value={data.cdConclusaoServico || ''}
        />
      )}
      <Textarea
        labelColor="#4F4F4F"
        label="Anotações"
        name="annotations"
        onChange={(e: any) =>
          setData((prev) => ({ ...prev, anotacoes: e.target.value }))
        }
        value={data.anotacoes}
      />

      <Button disabled={loading}>{loading ? 'Salvando...' : 'Salvar'}</Button>
    </FormStyled>
  )
}

export default ServiceForm
